import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from './supabaseClient'
import EmptyState from './ui/EmptyState'
import ErrorState from './ui/ErrorState'
import LoadingState from './ui/LoadingState'
import SectionHeader from './ui/SectionHeader'
import { ADMIN_ACCESS_ROLES, ADMIN_WORK_STATUSES, formatAccessRoleLabel, formatWorkStatusLabel } from './admin/adminConfig'
import { normalizeAccessRole } from './workforceAccessModel'

const REASON_OPTIONS = [
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'role_change', label: 'Role Change' },
  { value: 'status_change', label: 'Status Change' },
  { value: 'binding_repair', label: 'Binding Repair' },
  { value: 'admin_correction', label: 'Admin Correction' }
]

const PROTECTED_ACCOUNT_BLOCK_MESSAGE = 'Protected accounts cannot be modified from Access Admin.'

export default function AdminIdentityCleanupPanel() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [profiles, setProfiles] = useState([])
  const [workers, setWorkers] = useState([])
  const [history, setHistory] = useState([])
  const [savingProfileId, setSavingProfileId] = useState('')
  const [message, setMessage] = useState('')
  const [drafts, setDrafts] = useState({})

  const load = useCallback(async () => {
    setLoading(true)
    setError('')

    const [profilesResponse, workersResponse, historyResponse] = await Promise.all([
      supabase.from('profiles').select('*').order('display_name'),
      supabase.from('workers').select('id, name').order('name'),
      supabase.from('profile_access_status_history').select('*').order('created_at', { ascending: false }).limit(300)
    ])

    if (profilesResponse.error || workersResponse.error) {
      setError(profilesResponse.error?.message || workersResponse.error?.message || 'Unable to load access records.')
      setProfiles([])
      setWorkers([])
      setHistory([])
      setLoading(false)
      return
    }

    const rows = profilesResponse.data || []
    setProfiles(rows)
    setWorkers(workersResponse.data || [])
    setHistory(historyResponse.error ? [] : historyResponse.data || [])

    const nextDrafts = {}
    rows.forEach((profile) => {
      nextDrafts[String(profile.id)] = {
        access_role: normalizeAccessRole(profile.access_role || profile.role || 'employee'),
        work_status: String(profile.work_status || 'active'),
        worker_id: profile.worker_id ? String(profile.worker_id) : '',
        reason_code: '',
        reason_note: ''
      }
    })
    setDrafts(nextDrafts)
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const historyByProfileId = useMemo(() => {
    const map = new Map()
    history.forEach((row) => {
      const profileId = String(row.profile_id || '')
      if (!profileId || map.has(profileId)) return
      map.set(profileId, row)
    })
    return map
  }, [history])

  const updateDraft = (profileId, field, value) => {
    setDrafts((previous) => ({
      ...previous,
      [profileId]: {
        ...(previous[profileId] || {}),
        [field]: value
      }
    }))
  }

  const handleSave = async (profile) => {
    const profileId = String(profile.id)
    const draft = drafts[profileId]
    if (!draft) return

    if (!draft.reason_code) {
      setMessage('Reason code is required for role/status updates.')
      return
    }

    setSavingProfileId(profileId)
    setMessage('')

    const protectedCheckResponse = await supabase.rpc('is_protected_account', {
      target_email: null,
      target_profile_id: profile.id
    })

    if (protectedCheckResponse.error) {
      setSavingProfileId('')
      setMessage(`Unable to verify protected account status: ${protectedCheckResponse.error.message}. Update blocked until the protection check succeeds.`)
      return
    }

    if (protectedCheckResponse.data === true) {
      setSavingProfileId('')
      setMessage(PROTECTED_ACCOUNT_BLOCK_MESSAGE)
      return
    }

    const updatePayload = {
      work_status: draft.work_status,
      worker_id: draft.worker_id || null
    }

    if (Object.prototype.hasOwnProperty.call(profile, 'access_role')) {
      updatePayload.access_role = draft.access_role
    }

    if (Object.prototype.hasOwnProperty.call(profile, 'role')) {
      updatePayload.role = draft.access_role
    }

    const updateResponse = await supabase.from('profiles').update(updatePayload).eq('id', profile.id)
    if (updateResponse.error) {
      setSavingProfileId('')
      setMessage('Unable to save profile update: ' + updateResponse.error.message)
      return
    }

    const historyResponse = await supabase.from('profile_access_status_history').insert([
      {
        profile_id: profile.id,
        previous_access_role: profile.access_role || profile.role || null,
        next_access_role: draft.access_role,
        previous_work_status: profile.work_status,
        next_work_status: draft.work_status,
        reason_code: draft.reason_code,
        reason_note: draft.reason_note || null
      }
    ])

    const auditResponse = await supabase.from('admin_action_audit').insert([
      {
        action_type: 'access_status_change',
        entity_type: 'profile',
        entity_id: String(profile.id),
        before_value: {
          access_role: profile.access_role || profile.role || null,
          role: profile.role || null,
          work_status: profile.work_status,
          worker_id: profile.worker_id
        },
        after_value: updatePayload,
        reason: `${draft.reason_code}${draft.reason_note ? `: ${draft.reason_note}` : ''}`,
        source: 'AdminIdentityCleanupPanel'
      }
    ])

    setSavingProfileId('')
    if (historyResponse.error || auditResponse.error) {
      const warnings = [historyResponse.error?.message, auditResponse.error?.message].filter(Boolean).join(' | ')
      setMessage(`Access profile updated with audit warning: ${warnings}`)
    } else {
      setMessage('Access profile updated.')
    }
    await load()
  }

  if (loading) return <LoadingState title="Loading access admin" message="Preparing profile role/status records." />
  if (error) return <ErrorState title="Access admin unavailable" message={error} />

  return (
    <section style={{ display: 'grid', gap: 12 }}>
      <SectionHeader
        eyebrow="Admin"
        title="Role + Access Management"
        description="Update access roles, work status, and worker bindings with reason-coded history. Protected accounts are blocked from mutation."
      />

      {profiles.length === 0 ? (
        <EmptyState title="No access profiles" message="Profiles table returned no records." />
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {profiles.map((profile) => {
            const profileId = String(profile.id)
            const draft = drafts[profileId] || {}
            const latestHistory = historyByProfileId.get(profileId)

            return (
              <article key={profile.id} style={{ border: '1px solid #dce6df', borderRadius: 8, padding: 12 }}>
                <p style={{ margin: '0 0 6px' }}><strong>{profile.display_name || profile.id}</strong></p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                  <label>
                    Access Role
                    <select value={draft.access_role || ''} onChange={(event) => updateDraft(profileId, 'access_role', event.target.value)}>
                      {ADMIN_ACCESS_ROLES.map((role) => (
                        <option key={role} value={role}>{formatAccessRoleLabel(role)}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Work Status
                    <select value={draft.work_status || ''} onChange={(event) => updateDraft(profileId, 'work_status', event.target.value)}>
                      {ADMIN_WORK_STATUSES.map((status) => (
                        <option key={status} value={status}>{formatWorkStatusLabel(status)}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Worker Binding
                    <select value={draft.worker_id || ''} onChange={(event) => updateDraft(profileId, 'worker_id', event.target.value)}>
                      <option value="">Unbound</option>
                      {workers.map((worker) => (
                        <option key={worker.id} value={worker.id}>{worker.name || worker.id}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Reason Code
                    <select value={draft.reason_code || ''} onChange={(event) => updateDraft(profileId, 'reason_code', event.target.value)}>
                      <option value="">Select reason</option>
                      {REASON_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Reason Note
                    <input value={draft.reason_note || ''} onChange={(event) => updateDraft(profileId, 'reason_note', event.target.value)} />
                  </label>
                </div>
                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <p style={{ margin: 0, color: '#425347' }}>
                    Latest change: {latestHistory ? `${latestHistory.reason_code || 'n/a'} @ ${latestHistory.created_at || 'unknown'}` : 'No history'}
                  </p>
                  <button type="button" onClick={() => handleSave(profile)} disabled={savingProfileId === profileId}>
                    {savingProfileId === profileId ? 'Saving...' : 'Save Access Update'}
                  </button>
                </div>
              </article>
            )
          })}
        </div>
      )}

      {message ? <p>{message}</p> : null}
    </section>
  )
}
